import { Text, Title } from "@clipboard-health/ui-components";
import { Checkbox, DialogContent, FormControlLabel, Stack, TextField } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useFormContext } from "react-hook-form";

import { type PostShiftReviewFormValues, type StepAction } from "../types";

const MIN_REVIEW_LENGTH = 10;
const MIN_REVIEW_LENGTH_DELAYED = 10;
const MAX_REVIEW_LENGTH = 300;
const MAX_REVIEW_LENGTH_DELAYED = 1000;

export interface WriteReviewStepProps {
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
  isDelayedReview?: boolean;
}

export function WriteReviewStep(props: WriteReviewStepProps) {
  const { isSubmitting = false, onAction, isDelayedReview = false } = props;
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();
  const reviewText = watch("reviewText");
  const isAnonymous = watch("isAnonymous");

  const minLength = isDelayedReview ? MIN_REVIEW_LENGTH_DELAYED : MIN_REVIEW_LENGTH;
  const maxLength = isDelayedReview ? MAX_REVIEW_LENGTH_DELAYED : MAX_REVIEW_LENGTH;
  // For delayed reviews, don't count trailing spaces toward minimum length
  const effectiveLength = isDelayedReview ? reviewText.trimEnd().length : reviewText.length;
  const isValid = effectiveLength >= minLength && reviewText.length <= maxLength;
  const isOverLimit = reviewText.length > maxLength;

  function getHelperText() {
    if (isOverLimit) {
      return `${reviewText.length}/${maxLength} characters (too long)`;
    }

    if (effectiveLength < minLength) {
      return `${effectiveLength}/${maxLength} characters (${
        minLength - effectiveLength
      } more needed)`;
    }

    return `${effectiveLength}/${maxLength} characters`;
  }

  const title = "Leave your thoughts";
  const subtitle = isDelayedReview
    ? "Share details about the workplace and the shift to help other professionals know what to expect."
    : undefined;
  const placeholder = isDelayedReview
    ? "Share details about your experience — what worked well, what didn't, or anything worth noting."
    : "Please review the shift and the workplace. What should a new worker at this workplace know?";
  const disclaimer = isDelayedReview
    ? "Reviews that use inappropriate language or certain references to patients and specific workplace personnel will be removed. Repeated violations may impact your account."
    : "Reviews that use inappropriate language or certain references to patients and specific facility personnel will be removed. Repeat offenders may be banned from the marketplace";

  return (
    <>
      <DialogContent sx={{ py: 2, display: "flex", flexDirection: "column", gap: 6 }}>
        <Stack spacing={3}>
          <Title variant="h3" component="h3" textAlign="center">
            {title}
          </Title>
          {subtitle && (
            <Text variant="body2" textAlign="center" color="text.secondary">
              {subtitle}
            </Text>
          )}
        </Stack>

        <Stack spacing={4}>
          <TextField
            multiline
            rows={8}
            placeholder={placeholder}
            value={reviewText}
            disabled={isSubmitting}
            error={isOverLimit}
            helperText={getHelperText()}
            sx={{ pt: 5 }}
            onChange={(event) => {
              setValue("reviewText", event.target.value, { shouldValidate: true });
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous ?? false}
                disabled={isSubmitting}
                onChange={(event) => {
                  setValue("isAnonymous", event.target.checked, { shouldValidate: true });
                }}
              />
            }
            label={<Text>I want my review to be anonymous</Text>}
          />
        </Stack>

        <Text variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
          {disclaimer}
        </Text>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          isLoading={isSubmitting}
          disabled={!isValid}
          onClick={() => {
            onAction({
              type: "NEXT_STEP",
              data: { reviewText },
            });
          }}
        >
          Continue
        </LoadingButton>
      </DialogFooter>
    </>
  );
}
